// Track 2.3 — side-by-side position comparison card.
//
// Surfaces when the rider has held two distinct positions for >=30s each
// and the position they just left was *not* their best of the session.
// Shows the current (most recent) hold next to the best-so-far, with the
// watts delta at 40 km/h.
//
// Auto-mounts in AIClientVision's stats sidebar. Hidden when no comparison
// is available.

import { refAeroSpeedLabel, useUnitSystem } from '../services/useUnitSystem';
import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ABComparison } from '../vision/positionSegmenter';
import { colors } from '../theme';
import { labelForPosition } from '../vision/positionClassifier';

interface Props {
  ab: ABComparison;
}

export default function PositionComparison({ ab }: Props) {
  const units = useUnitSystem();
  const { t } = useTranslation('session');
  const current = ab.current;
  const best = ab.best;
  const wattsAbs = Math.round(Math.abs(ab.wattsDelta));

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Typography
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: colors.textMuted,
          mb: 1.5,
        }}
      >
        {t('ab.title')}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <PositionCard
          label={t('ab.current')}
          positionClass={current.positionClass}
          cda={current.cdaBest}
          highlight={current.cdaBest <= best.cdaBest}
        />
        <PositionCard
          label={t('ab.best')}
          positionClass={best.positionClass}
          cda={best.cdaBest}
          highlight={best.cdaBest < current.cdaBest}
        />
      </Box>

      <Box
        sx={{
          mt: 2,
          pt: 1.5,
          borderTop: `1px solid ${colors.border}`,
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: '0.8125rem',
            color: colors.text,
            fontWeight: 600,
          }}
        >
          {t('ab.delta', {
            refSpeed: refAeroSpeedLabel(units),
            watts: wattsAbs,
            best: labelForPosition(best.positionClass as any),
          })}
        </Typography>
      </Box>
    </Box>
  );
}

function PositionCard({
  label,
  positionClass,
  cda,
  highlight,
}: {
  label: string;
  positionClass: string;
  cda: number;
  highlight: boolean;
}) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        backgroundColor: highlight ? 'rgba(106, 255, 69, 0.06)' : colors.bgSubtle,
        border: `1px solid ${highlight ? colors.borderFocus : colors.border}`,
      }}
    >
      <Typography
        sx={{
          fontSize: '0.625rem',
          fontWeight: 600,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: highlight ? colors.brand : colors.textMuted,
          mb: 0.75,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.8125rem',
          fontWeight: 600,
          color: colors.text,
          mb: 0.25,
        }}
      >
        {labelForPosition(positionClass as any)}
      </Typography>
      <Typography
        sx={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '1.125rem',
          fontWeight: 700,
          color: highlight ? colors.brand : colors.text,
        }}
      >
        {cda.toFixed(3)}{' '}
        <Typography
          component="span"
          sx={{ fontSize: '0.6875rem', color: colors.textMuted }}
        >
          m²
        </Typography>
      </Typography>
    </Box>
  );
}
